import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { apolloClient } from "@/api/apiClient";
import { SUPPORT_TOKEN_QUERY } from "@/codegen/graphql/queries/SupportToken.query";
import { useAuthStore, useIsLoggedIn } from "@/stores/useAuth";
import {
  useBtsPlayerOpen,
  usePaywallOpen,
  useUpsellOpen,
} from "@/stores/useAppState";
import {
  hideZendesk,
  loginZendeskUser,
  logoutZendeskUser,
  setZendeskFields,
  setZendeskTags,
  showZendesk,
} from "@/lib/zendesk";
import { ZENDESK_FIELD_IDS } from "@/lib/zendeskFields";
import { getDeviceContext } from "@/lib/userAgent";

/** Fetch a fresh Zendesk JWT for the current user. */
const fetchSupportToken = async (): Promise<string | null> => {
  try {
    const { data } = await apolloClient.query({
      query: SUPPORT_TOKEN_QUERY,
      fetchPolicy: "network-only",
    });
    return data?.supportToken.token ?? null;
  } catch (error) {
    console.error("Failed to fetch the Zendesk support token:", error);
    return null;
  }
};

/**
 * Drives the Zendesk Web Widget for the whole app: loads it once, authenticates
 * the user when logged in (anonymous otherwise), tags the conversation with
 * support context, and hides it on the fullscreen player and whenever a paywall
 * is open (it would otherwise overlap the paywall's bottom CTA).
 */
const useZendesk = () => {
  const isLoggedIn = useIsLoggedIn();
  const isPremium = useAuthStore((s) => !!s.currentUser?.premium?.productId);
  const trackingId = useAuthStore((s) => s.currentUser?.trackingId);
  const isPaywallOpen = usePaywallOpen();
  const isUpsellOpen = useUpsellOpen();
  const isBtsPlayerOpen = useBtsPlayerOpen();
  const isVideoPlayer = useRouterState({
    select: (s) => s.matches.some((m) => m.routeId.startsWith("/_videoPlayer")),
  });

  // The widget loads lazily on the first command below (loadZendesk is
  // idempotent and memoized), so no explicit load effect is needed.
  useEffect(() => {
    if (isLoggedIn) loginZendeskUser(fetchSupportToken);
    else logoutZendeskUser();
  }, [isLoggedIn]);

  useEffect(() => {
    setZendeskTags([
      "platform_web",
      `app_version_${__APP_VERSION__}`,
      isPremium ? "vip" : "free",
    ]);
  }, [isPremium]);

  useEffect(() => {
    // Required Metadata exposed as conversation (ticket) fields, keyed by the
    // numeric Zendesk field IDs. browser/device type are best-effort from the
    // user-agent (src/lib/userAgent.ts).
    const { browser, deviceType } = getDeviceContext();
    setZendeskFields([
      // user id is present once currentUser is loaded (incl. anonymous device
      // users, which also carry a trackingId); omitted only before it resolves.
      ...(trackingId
        ? [{ id: ZENDESK_FIELD_IDS.userId, value: trackingId }]
        : []),
      { id: ZENDESK_FIELD_IDS.appVersion, value: __APP_VERSION__ },
      { id: ZENDESK_FIELD_IDS.platform, value: "web" },
      { id: ZENDESK_FIELD_IDS.browser, value: browser },
      { id: ZENDESK_FIELD_IDS.deviceType, value: deviceType },
      {
        // Drop-down field: only its predefined tags are accepted, anything else
        // errors the widget. The client only knows premium as a boolean, so the
        // retention/dunning/winback tags it also allows are never sent.
        id: ZENDESK_FIELD_IDS.subscriptionStatus,
        value: isPremium ? "subscription_active" : "subscription_inactive",
      },
    ]);
  }, [trackingId, isPremium]);

  useEffect(() => {
    // Hidden on the fullscreen player and on the paywall (it would cover the
    // paywall's bottom "Get Full Access" CTA), and over a BTS video.
    if (isVideoPlayer || isPaywallOpen || isUpsellOpen || isBtsPlayerOpen)
      hideZendesk();
    else showZendesk();
  }, [isVideoPlayer, isPaywallOpen, isUpsellOpen, isBtsPlayerOpen]);
};

export default useZendesk;
